import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { AUDIENCES, PROGRAMME_PATH, SITE } from "@/lib/content";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { Leaf } from "@/components/site/Leaf";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-7", "lg:col-span-5"];

function ServiceCard({ i }: { i: number }) {
  const a = AUDIENCES[i];
  const photo = i % 3 === 0 || i === 4;
  return (
    <Reveal delay={(i % 2) * 0.1} className={`${SPANS[i]} h-full`}>
      <Link
        to={`/${a.slug}`}
        data-testid={`service-card-${a.slug}`}
        className={`group relative flex h-full min-h-[300px] flex-col justify-between overflow-hidden rounded-[30px] p-7 transition-transform duration-700 ease-[cubic-bezier(.22,1,.36,1)] hover:-translate-y-1.5 sm:min-h-[340px] sm:p-9 ${photo ? "text-cream" : "bg-cream-sand text-forest ring-1 ring-forest/10"}`}
      >
        {photo ? (
          <>
            <img src={a.image} alt={a.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-tr from-forest-deep/90 via-forest-deep/50 to-transparent" />
          </>
        ) : (
          <Leaf className="absolute -bottom-10 -right-6 h-48 w-48 rotate-[30deg] text-forest/[0.07] transition-transform duration-1000 group-hover:rotate-[50deg]" />
        )}
        <div className="relative flex items-start justify-between">
          <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${photo ? "text-gold-soft" : "text-gold-ink"}`}>{a.pillar}</span>
          <span className={`grid h-11 w-11 place-items-center rounded-full transition-transform duration-500 group-hover:rotate-45 ${photo ? "bg-cream text-forest" : "bg-forest text-cream"}`}>
            <ArrowUpRight className="h-5 w-5" />
          </span>
        </div>
        <div className="relative">
          <h3 className="max-w-md font-display text-4xl leading-[1.02] sm:text-5xl">{a.title}</h3>
          <p className={`mt-3 max-w-sm text-sm leading-relaxed sm:text-base ${photo ? "text-cream/90" : "text-charcoal-soft"}`}>{a.short}</p>
        </div>
      </Link>
    </Reveal>
  );
}

export function Services() {
  return (
    <section data-testid="services-section" className="bg-cream pb-24 sm:pb-32">
      <div className="wrap">
        <p className="eyebrow">Programmes</p>
        <h2 className="mt-5 max-w-3xl text-5xl leading-[0.98] text-forest sm:text-6xl lg:text-7xl">
          <MaskLines lines={["One programme,", <em key="s" className="text-pine">shaped around you.</em>]} />
        </h2>

        <Reveal className="mt-12">
          <Link to={PROGRAMME_PATH} data-testid="featured-programme-card" className="grain group relative grid overflow-hidden rounded-[30px] bg-forest p-7 text-cream sm:p-10 lg:grid-cols-12 lg:items-center lg:gap-10">
            <div className="relative lg:col-span-8">
              <p className="eyebrow-dark">Signature programme</p>
              <h3 className="mt-4 font-display text-4xl leading-tight sm:text-6xl">Healthy Active Lifestyle Programme</h3>
              <p className="mt-4 max-w-xl text-base text-cream/85">120 days of personal coaching across Nourish, Energise and Transform. The foundation of every plan below.</p>
            </div>
            <div className="relative mt-8 flex items-end justify-between gap-6 lg:col-span-4 lg:mt-0 lg:flex-col lg:items-end">
              <p className="font-display text-5xl text-gold-soft sm:text-6xl" data-testid="featured-programme-price">{SITE.price}<span className="text-xl text-cream/80"> /month</span></p>
              <span className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-cream px-5 py-3 text-sm font-semibold text-forest transition-transform duration-500 group-hover:translate-x-1">
                {SITE.days} days <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        </Reveal>

        <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-12">
          {AUDIENCES.map((a, i) => <ServiceCard key={a.slug} i={i} />)}
        </div>
      </div>
    </section>
  );
}
